import React from "react";
import { SurveySegment } from "../../../../../../amorg-common";
import { Card } from "../../../../../../designSystem/Card";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { OnOff } from "../../../../../../designSystem/inputs/OnOff";
import { Table } from "../../../../../../designSystem/Table";
import { SegmentationViewMode } from "../../../../../../utils/amorgLocalStorage";
import { SegmentListChip } from "./SegmentListChip";
import { InfoChip } from "../../../../../../designSystem/Chip";

export type SegmentListViewData = {
    segments: SurveySegment[],
    identifier: string,
    selected: boolean,
    answerCount: number;
};

type BaseSegmentListViewProps = {
    segmentLists: SegmentListViewData[],
    displayAnswerCount?: boolean,
    noSelectionContent?: React.ReactNode,
    localStorageViewModeKey?: string,
    onToggleSelect?: (identifier: string, selected: boolean) => void,
    onToggleAll?(): void
};

type SegmentListViewProps =
    BaseSegmentListViewProps &
    {
        view?: SegmentationViewMode,
        wrapInCard?: boolean;
    };

export function SegmentListView (props: SegmentListViewProps): JSX.Element 
{
    return props.wrapInCard
        ?
        <Card variant="outlined">
            <InternalSegmentListView {...props} />
        </Card>
        :
        <InternalSegmentListView {...props} />;
}

export function InternalSegmentListView (props: SegmentListViewProps): JSX.Element 
{ 

    return (
        <>
            {props.segmentLists.length > 0 &&
                <Flex flexDirection="column" gap={16}>
                    
                    {props.segmentLists.length > 0 && props.view === 'pills' &&
                        <SegmentListChipsView segmentLists={props.segmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={props.onToggleSelect}
                            onToggleAll={props.onToggleAll} />
                    }

                    {props.segmentLists.length > 0 && props.view === 'table' &&
                        <SegmentListTableView segmentLists={props.segmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={props.onToggleSelect}
                            onToggleAll={props.onToggleAll} />
                    }

                    {props.segmentLists.length === 0 &&
                        <Flex style={{ fontSize: '16px', paddingBottom: '24px' }} justifyContent="center">
                            {props.noSelectionContent || <i>Ingen segmentlister at vise</i>}
                        </Flex>
                    }

                </Flex>
            }
        </>
    );
}

function SegmentListChipsView (props: BaseSegmentListViewProps): JSX.Element
{
    function handleClick (identifier: string, selected: boolean)
    {
        if (props.onToggleSelect)
            props.onToggleSelect(identifier, selected);
    }

    const shownSegmentLists = React.useMemo(() => {
        if (props.onToggleSelect) {
            return props.segmentLists;
        }
        else {
            return props.segmentLists.filter(sl => sl.selected);
        }
    }, [props.segmentLists, props.onToggleSelect]);

    return (
        <section>
            <Flex gap={4} style={{ flexWrap: 'wrap' }}>
                {shownSegmentLists.map((l, index) =>
                    <SegmentListChip key={l.identifier + '_' + index}
                        identifier={l.identifier}
                        segments={l.segments}
                        selected={l.selected}
                        answerCount={props.displayAnswerCount ? l.answerCount : undefined}
                        onClick={props.onToggleSelect ? handleClick : undefined} />
                )}
            </Flex>
        </section>
    );
}

function SegmentListTableView (props: BaseSegmentListViewProps): JSX.Element
{
    if (!props.segmentLists)
        return <></>;

    function handleToggleClick (identifier: string, selected: boolean)
    {
        if (props.onToggleSelect)
            props.onToggleSelect(identifier, selected);
    }

    return (
        <>
            <Table style={{ overflowX: 'auto' }}>
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>
                            {props.onToggleSelect && props.onToggleAll &&
                                <OnOff size="small" active={props.segmentLists.every(s => s.selected)} onClick={props.onToggleAll} />
                            }
                        </th>
                        <th>Segmenter</th>
                        {props.displayAnswerCount &&
                            <th>Antal besvarelser</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    {props.segmentLists.map(sl =>
                        <tr key={sl.identifier}>
                            <td>
                                {props.onToggleSelect &&
                                    <SegmentToggleCell identifier={sl.identifier} selected={sl.selected} onToggleClick={handleToggleClick} />
                                }
                            </td>
                            <td>
                                <Flex gap={4} style={{ flexWrap: 'wrap' }}>
                                    {sl.segments.map((s, index) =>
                                        <InfoChip key={sl.identifier + '-' + index} variant={sl.selected ? 'filled' : 'outlined'}>
                                            {s.value}
                                        </InfoChip>
                                    )}

                                </Flex>

                            </td>
                            {props.displayAnswerCount &&
                                <td>{sl.answerCount}</td>
                            }
                        </tr>
                    )}
                </tbody>
            </Table >
        </>
    );
}

function SegmentToggleCell (props: { identifier: string, selected: boolean, onToggleClick?:(identifier:string, selected:boolean)=>void }): JSX.Element
{
    function handleClick ()
    {
        if (props.onToggleClick)
            props.onToggleClick(props.identifier, !props.selected);
    }

    return (
        <>
            {props.onToggleClick &&
                <OnOff size="small" active={props.selected} onClick={handleClick} />
            }
        </>
    );
}